import { ONNXFaceDetectionService } from './onnxService';
import { BackgroundRemovalService } from './backgroundRemovalService';
import { 
  FaceDetection, 
  ProcessingResult, 
  ProcessingStatus, 
  ProcessingState, 
  DeviceCapabilities,
  ProcessingMode,
  ClientProcessorOptions
} from '../../types/client';
import { Country } from '../../types';
import { addDPIMetadata } from '../../utils/dpiUtils';
import { debug } from '../../utils/debug';

export class ClientImageProcessor {
  private faceDetectionService: ONNXFaceDetectionService;
  private backgroundRemovalService: BackgroundRemovalService;
  private capabilities: DeviceCapabilities;
  private options: ClientProcessorOptions;
  private currentStatus: ProcessingStatus;
  private statusCallbacks: ((status: ProcessingStatus) => void)[] = [];

  constructor(options: Partial<ClientProcessorOptions> = {}) {
    this.capabilities = this.detectDeviceCapabilities();
    
    this.options = {
      mode: 'client',
      fallbackToServer: true,
      enableProgressLoading: true,
      maxModelCacheSize: 100 * 1024 * 1024, // 100MB
      ...options
    };

    this.currentStatus = {
      state: ProcessingState.IDLE,
      progress: 0,
      message: 'Ready'
    };

    this.faceDetectionService = new ONNXFaceDetectionService();
    // Try demo mode if explicitly requested or as fallback
    const demoMode = options.demoMode || false;
    this.backgroundRemovalService = new BackgroundRemovalService(this.capabilities, { demoMode });

    debug.processor('🖥️ Client image processor initialized');
    debug.processor('  - Device capabilities:', this.capabilities);
    debug.processor('  - Processing mode:', this.options.mode);
    debug.processor('  - Demo mode:', demoMode ? 'enabled' : 'disabled');
  }

  private detectDeviceCapabilities(): DeviceCapabilities {
    return {
      isMobile: /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
      availableMemory: (navigator as any).deviceMemory || 4,
      hardwareConcurrency: navigator.hardwareConcurrency || 4,
      webglSupported: this.isWebGLSupported(),
      webgpuSupported: 'gpu' in navigator,
      connection: (navigator as any).connection?.effectiveType
    };
  }

  private isWebGLSupported(): boolean {
    try {
      const canvas = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && canvas.getContext('webgl'));
    } catch {
      // In headless environments (like tests), assume WebGL is supported
      return true;
    }
  }

  private updateStatus(state: ProcessingState, progress: number, message: string, error?: string): void {
    this.currentStatus = { state, progress, message, error };
    this.statusCallbacks.forEach(callback => callback(this.currentStatus));
  }

  onStatusUpdate(callback: (status: ProcessingStatus) => void): () => void {
    this.statusCallbacks.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.statusCallbacks.indexOf(callback);
      if (index > -1) {
        this.statusCallbacks.splice(index, 1);
      }
    };
  }

  async initialize(): Promise<void> {
    try {
      this.updateStatus(ProcessingState.LOADING_MODELS, 0, 'Initializing AI models...');

      // Initialize services with graceful error handling
      const initPromises = [];
      let faceDetectionError: any = null;
      let backgroundRemovalError: any = null;

      // Skip face detection for now - users will select area manually
      faceDetectionError = new Error('Face detection disabled - manual selection enabled');
      debug.log('📝 Face detection disabled - users will select area manually');

      // Try to initialize background removal service
      initPromises.push(
        this.backgroundRemovalService.initialize(
          (progress) => this.updateStatus(ProcessingState.LOADING_MODELS, 50 + (progress * 0.5), 'Loading background removal model...')
        ).catch(error => {
          backgroundRemovalError = error;
          debug.warn('⚠️ Background removal service failed to initialize:', error?.message || 'Unknown error');
        })
      );

      await Promise.allSettled(initPromises);

      // Determine initialization status
      // Check if face detection was intentionally disabled
      const faceDetectionDisabled = faceDetectionError?.message?.includes('Face detection disabled');
      
      if (faceDetectionError && backgroundRemovalError && !faceDetectionDisabled) {
        const faceMsg = faceDetectionError?.message || 'Unknown error';
        const bgMsg = backgroundRemovalError?.message || 'Unknown error';
        throw new Error(`Both services failed to initialize. Face detection: ${faceMsg}. Background removal: ${bgMsg}`);
      } else if (backgroundRemovalError) {
        const errorMsg = backgroundRemovalError?.message || 'Unknown error';
        debug.warn('⚠️ Background removal failed, falling back to demo mode:', errorMsg);
        
        // Automatically fall back to demo mode if real background removal fails
        this.backgroundRemovalService = new BackgroundRemovalService(this.capabilities, { demoMode: true });
        await this.backgroundRemovalService.initialize();
        
        this.updateStatus(ProcessingState.IDLE, 100, 'Background removal ready (demo mode)');
        debug.log('✓ Client image processor initialized with demo background removal');
      } else if (faceDetectionDisabled) {
        this.updateStatus(ProcessingState.IDLE, 100, 'Manual selection mode ready');
        debug.log('✓ Client image processor initialized - Manual selection mode enabled');
      } else if (faceDetectionError) {
        const errorMsg = faceDetectionError?.message || 'Unknown error';
        this.updateStatus(ProcessingState.IDLE, 100, 'Background removal ready (face detection unavailable)');
        debug.warn('⚠️ Running with background removal only - face detection failed');
      } else {
        this.updateStatus(ProcessingState.IDLE, 100, 'All models loaded successfully');
        debug.log('✓ Client image processor fully initialized');
      }

    } catch (error) {
      this.updateStatus(ProcessingState.ERROR, 0, 'Failed to initialize models', error instanceof Error ? error.message : 'Unknown error');
      throw error;
    }
  }

  // New method that only prepares the original image without background removal
  async prepareOriginalImage(file: File, country: Country): Promise<ProcessingResult> {
    try {
      debug.log(`📸 Preparing original image for ${country.name} (${country.code}) - No background removal`);
      const startTime = performance.now();

      // Convert file to ImageData
      this.updateStatus(ProcessingState.DETECTING_FACES, 0, 'Preparing image for editing...');
      const imageData = await this.fileToImageData(file);

      // Skip face detection and background removal
      this.updateStatus(ProcessingState.DETECTING_FACES, 80, 'Image ready for editing...');
      const faces: FaceDetection[] = [];

      // Use original image as both original and "background removed" initially
      this.updateStatus(ProcessingState.GENERATING_PREVIEW, 95, 'Opening photo editor...');
      
      const totalTime = performance.now() - startTime;
      
      const result: ProcessingResult = {
        originalImage: imageData,
        backgroundRemoved: imageData, // Same as original - background removal will happen on demand
        faces, // Empty since we're doing manual selection
        processedImage: file, // Keep original file for later background removal
        metadata: {
          processingTime: totalTime,
          modelUsed: 'original-image-only (background removal on demand)',
          deviceInfo: this.capabilities
        }
      };

      this.updateStatus(ProcessingState.COMPLETE, 100, 'Photo editor ready');
      debug.log(`✓ Original image prepared in ${totalTime.toFixed(2)}ms - background removal available on demand`);

      return result;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      this.updateStatus(ProcessingState.ERROR, 0, 'Failed to prepare original image', errorMessage);
      
      if (this.options.fallbackToServer) {
        debug.log('🔄 Falling back to server processing...');
        throw new Error(`Client processing failed, server fallback needed: ${errorMessage}`);
      }
      
      throw error;
    }
  }

  async processImage(file: File, country: Country): Promise<ProcessingResult> {
    try {
      debug.log(`🎯 Processing image for ${country.name} (${country.code}) - Manual selection mode`);
      const startTime = performance.now();

      // Convert file to ImageData
      this.updateStatus(ProcessingState.DETECTING_FACES, 0, 'Preparing image...');
      const imageData = await this.fileToImageData(file);

      // Step 1: Skip face detection - return for manual selection
      this.updateStatus(ProcessingState.DETECTING_FACES, 30, 'Image ready for manual selection...');
      
      // Create empty faces array since we're doing manual selection
      const faces: FaceDetection[] = [];

      // Step 2: Remove background
      this.updateStatus(ProcessingState.REMOVING_BACKGROUND, 40, 'Removing background...');
      const backgroundRemovedBlob = await this.backgroundRemovalService.removeBackground(
        file,
        (progress) => this.updateStatus(ProcessingState.REMOVING_BACKGROUND, 40 + (progress * 0.5), 'Removing background...')
      );

      // Step 3: Prepare result for manual selection
      this.updateStatus(ProcessingState.GENERATING_PREVIEW, 90, 'Preparing for manual selection...');
      const backgroundRemovedImageData = await BackgroundRemovalService.blobToImageData(backgroundRemovedBlob);
      
      const totalTime = performance.now() - startTime;
      
      const result: ProcessingResult = {
        originalImage: imageData,
        backgroundRemoved: backgroundRemovedImageData,
        faces, // Empty since we're doing manual selection
        processedImage: backgroundRemovedBlob, // Background removed, ready for manual cropping
        metadata: {
          processingTime: totalTime,
          modelUsed: 'background-removal-js (manual selection)',
          deviceInfo: this.capabilities
        }
      };

      this.updateStatus(ProcessingState.COMPLETE, 100, 'Background removed - ready for manual selection');
      debug.log(`✓ Background removal completed in ${totalTime.toFixed(2)}ms - ready for manual selection`);

      return result;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      this.updateStatus(ProcessingState.ERROR, 0, 'Processing failed', errorMessage);
      
      if (this.options.fallbackToServer) {
        debug.log('🔄 Falling back to server processing...');
        // Here you would implement fallback to server processing
        throw new Error(`Client processing failed, server fallback needed: ${errorMessage}`);
      }
      
      throw error;
    }
  }

  // On-demand background removal method
  async removeBackgroundFromFile(file: File | Blob, onProgress?: (progress: number) => void): Promise<ImageData> {
    try {
      debug.log('🎨 Starting on-demand background removal...');
      const startTime = performance.now();

      this.updateStatus(ProcessingState.REMOVING_BACKGROUND, 0, 'Removing background...');
      
      // Remove background
      const backgroundRemovedBlob = await this.backgroundRemovalService.removeBackground(
        file,
        (progress) => {
          this.updateStatus(ProcessingState.REMOVING_BACKGROUND, progress, 'Removing background...');
          if (onProgress) onProgress(progress);
        }
      );

      // Convert to ImageData
      const backgroundRemovedImageData = await BackgroundRemovalService.blobToImageData(backgroundRemovedBlob);
      
      const totalTime = performance.now() - startTime;
      this.updateStatus(ProcessingState.COMPLETE, 100, 'Background removal completed');
      debug.log(`✓ On-demand background removal completed in ${totalTime.toFixed(2)}ms`);

      return backgroundRemovedImageData;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      this.updateStatus(ProcessingState.ERROR, 0, 'Background removal failed', errorMessage);
      throw error;
    }
  }

  // New method for processing with manual selection - crops directly without scaling
  async processWithManualSelection(
    imageData: ImageData, 
    selection: { x: number; y: number; width: number; height: number },
    country: Country
  ): Promise<Blob> {
    try {
      debug.log(`🎯 Generating passport photo with manual selection for ${country.name}`);
      debug.log('📐 Selection coordinates:', selection);
      
      this.updateStatus(ProcessingState.FINALIZING, 0, 'Generating passport photo with your selection...');
      
      // Create passport photo directly from manual selection (no auto-scaling)
      const processedBlob = await this.generatePassportPhotoFromSelection(imageData, selection, country);
      
      this.updateStatus(ProcessingState.COMPLETE, 100, 'Passport photo generated successfully!');
      
      return processedBlob;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      this.updateStatus(ProcessingState.ERROR, 0, 'Failed to generate passport photo', errorMessage);
      throw error;
    }
  }

  // Generate passport photo directly from manual selection without auto-scaling
  private async generatePassportPhotoFromSelection(
    imageData: ImageData, 
    selection: { x: number; y: number; width: number; height: number },
    country: Country
  ): Promise<Blob> {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;

    // Set target dimensions
    canvas.width = country.photo_width;
    canvas.height = country.photo_height;

    // Fill with white background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Create temporary canvas for the source image
    const sourceCanvas = document.createElement('canvas');
    const sourceCtx = sourceCanvas.getContext('2d')!;
    sourceCanvas.width = imageData.width;
    sourceCanvas.height = imageData.height;
    sourceCtx.putImageData(imageData, 0, 0);

    debug.log('🖼️ Source image dimensions:', imageData.width, 'x', imageData.height);
    debug.log('📏 Target dimensions:', country.photo_width, 'x', country.photo_height);
    debug.log('✂️ Selection area:', selection);

    // Calculate scale to fit the selection into the target passport photo size
    const scaleX = canvas.width / selection.width;
    const scaleY = canvas.height / selection.height;
    const scale = Math.min(scaleX, scaleY); // Use smaller scale to ensure it fits

    // Calculate centered position
    const scaledWidth = selection.width * scale;
    const scaledHeight = selection.height * scale;
    const offsetX = (canvas.width - scaledWidth) / 2;
    const offsetY = (canvas.height - scaledHeight) / 2;

    debug.log('📐 Calculated scale:', scale);
    debug.log('📍 Final position:', offsetX, offsetY);
    debug.log('📏 Scaled size:', scaledWidth, 'x', scaledHeight);

    // Draw the selected area directly to the passport photo canvas
    ctx.drawImage(
      sourceCanvas,
      // Source coordinates (the selected area)
      selection.x, selection.y, selection.width, selection.height,
      // Destination coordinates (centered in passport photo)
      offsetX, offsetY, scaledWidth, scaledHeight
    );

    // Convert to blob with 300 DPI metadata
    return new Promise((resolve) => {
      canvas.toBlob(async (blob) => {
        if (blob) {
          const dpiBlob = await addDPIMetadata(blob, { dpi: 300 });
          resolve(dpiBlob);
        } else {
          resolve(blob!);
        }
      }, 'image/jpeg', 1.0);
    });
  }

  private async fileToImageData(file: File): Promise<ImageData> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        ctx.drawImage(img, 0, 0);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        resolve(imageData);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });
  }

  private async generatePassportPhoto(
    imageData: ImageData, 
    face: FaceDetection, 
    country: Country
  ): Promise<Blob> {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;

    // Set target dimensions
    canvas.width = country.photo_width;
    canvas.height = country.photo_height;

    // Fill with white background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Calculate optimal positioning (simplified version)
    const [x1, y1, x2, y2] = face.bbox;
    const faceWidth = x2 - x1;
    const faceHeight = y2 - y1;
    const faceCenterX = x1 + faceWidth / 2;
    const faceCenterY = y1 + faceHeight / 2;

    // Calculate scale to fit face properly in passport photo
    const targetFaceHeight = canvas.height * country.face_height_ratio;
    const scale = targetFaceHeight / faceHeight;

    // Calculate position to center the face
    const scaledImageWidth = imageData.width * scale;
    const scaledImageHeight = imageData.height * scale;
    const scaledFaceCenterX = faceCenterX * scale;
    const scaledFaceCenterY = faceCenterY * scale;

    // Position the head in the upper portion of the image
    const targetHeadCenterX = canvas.width / 2;
    const targetHeadCenterY = canvas.height * 0.35; // 35% from top

    const offsetX = targetHeadCenterX - scaledFaceCenterX;
    const offsetY = targetHeadCenterY - scaledFaceCenterY;

    // Create temporary canvas for the source image
    const sourceCanvas = document.createElement('canvas');
    const sourceCtx = sourceCanvas.getContext('2d')!;
    sourceCanvas.width = imageData.width;
    sourceCanvas.height = imageData.height;
    sourceCtx.putImageData(imageData, 0, 0);

    // Draw the scaled and positioned image
    ctx.drawImage(
      sourceCanvas,
      0, 0, imageData.width, imageData.height,
      offsetX, offsetY, scaledImageWidth, scaledImageHeight
    );

    // Convert to blob with 300 DPI metadata
    return new Promise((resolve) => {
      canvas.toBlob(async (blob) => {
        if (blob) {
          const dpiBlob = await addDPIMetadata(blob, { dpi: 300 });
          resolve(dpiBlob);
        } else {
          resolve(blob!);
        }
      }, 'image/jpeg', 1.0);
    });
  }

  async cropAndResize(
    imageData: ImageData,
    selection: { x: number; y: number; width: number; height: number },
    targetWidth: number,
    targetHeight: number
  ): Promise<Blob> {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;

    // Set up source canvas
    const sourceCanvas = document.createElement('canvas');
    const sourceCtx = sourceCanvas.getContext('2d')!;
    sourceCanvas.width = imageData.width;
    sourceCanvas.height = imageData.height;
    sourceCtx.putImageData(imageData, 0, 0);

    // Set target dimensions
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    // Fill with white background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, targetWidth, targetHeight);

    // Draw cropped and resized image
    ctx.drawImage(
      sourceCanvas,
      selection.x, selection.y, selection.width, selection.height,
      0, 0, targetWidth, targetHeight
    );

    // Convert to blob with 300 DPI metadata
    return new Promise((resolve) => {
      canvas.toBlob(async (blob) => {
        if (blob) {
          const dpiBlob = await addDPIMetadata(blob, { dpi: 300 });
          resolve(dpiBlob);
        } else {
          resolve(blob!);
        }
      }, 'image/jpeg', 1.0);
    });
  }

  getStatus(): ProcessingStatus {
    return { ...this.currentStatus };
  }

  getCapabilities(): DeviceCapabilities {
    return { ...this.capabilities };
  }

  isSupported(): boolean {
    return this.capabilities.webglSupported && this.capabilities.availableMemory >= 2;
  }

  dispose(): void {
    this.faceDetectionService.dispose();
    this.backgroundRemovalService.dispose();
    this.statusCallbacks = [];
    
    this.updateStatus(ProcessingState.IDLE, 0, 'Disposed');
    debug.log('🧹 Client image processor disposed');
  }
}